/**
 * Parwaaz backend — persistence and platform contracts.
 *
 * The core (handlers, state machine, auth) depends ONLY on these two interfaces.
 * Adapters:
 *   adapters/sheets  → Google Sheets + Drive + Apps Script Utilities (production)
 *   adapters/memory  → plain objects, optionally persisted to a JSON file (dev server) or IndexedDB (browser demo)
 *
 * All methods are synchronous by design: Apps Script has no async I/O, and the memory adapter is trivially sync.
 * Anything that must be async in the browser (IndexedDB) is loaded into memory at boot and flushed after each call.
 */
import type { Application, AuditRow, ScanRow, Staff } from '@parwaaz/shared';

export type { Application, AuditRow, ScanRow, Staff };
export type Card = import('@parwaaz/shared').CardRecord & { ref: string };

export interface ApplicationFilter {
  states?: string[];
  season?: string;
  search?: string; // matched against name, ref, doc number, card no (case-insensitive)
  limit?: number;
}

export class ConflictError extends Error {
  code = 'conflict' as const;
  constructor(message = 'this case changed, reload') {
    super(message);
  }
}
export class NotFoundError extends Error {
  code = 'not_found' as const;
}

export interface StoredFile {
  id: string;
  name: string;
  mime: string;
  data: string; // base64
  size: number;
}

export interface Store {
  // applications (the register)
  findByIdem(idemKey: string): Application | null;
  getApplication(ref: string): Application | null;
  appendApplication(app: Application): void;
  /** Applies `patch`, bumps updatedAt, and throws ConflictError if stored updatedAt !== expectedUpdatedAt (when given). */
  updateApplication(ref: string, patch: Partial<Application>, expectedUpdatedAt?: string): Application;
  listApplications(filter?: ApplicationFilter): Application[];
  /** Any active (non-revoked, non-expired) card this season for the same document number. */
  findActiveByDocNumber(docNumber: string, season: string): Application | null;

  // audit (append-only)
  appendAudit(row: Omit<AuditRow, 'seq'>): AuditRow;
  listAudit(entityId?: string): AuditRow[];

  // config (key/value; season, card_seq, key_id, thresholds, revocation_version …)
  getConfig(key: string): string | null;
  setConfig(key: string, value: string): void;
  allConfig(): Record<string, string>;

  // staff
  listStaff(): Staff[];
  getStaff(user: string): Staff | null;
  upsertStaff(staff: Staff): void;
  getStaffSecret(user: string): string | null; // SHA-256(derivedKey), base64url
  setStaffSecret(user: string, hash: string): void;

  // secrets that are not staff (SESSION_SECRET, provider keys)
  getSecret(name: string): string | null;
  setSecret(name: string, value: string): void;
  deleteSecret(name: string): void;

  // files (documents)
  putFile(ref: string, slot: string, name: string, mime: string, dataB64: string): string; // returns file id
  getFile(fileId: string): StoredFile | null;
  deleteFile(fileId: string): void;

  // cards
  allocateCardNo(prefix: string, seasonYear: string): string; // increments card_seq under the caller's lock
  listCards(): Card[];
  upsertCard(card: Card): void;

  // scans
  appendScans(rows: ScanRow[]): void;
  listScans(sinceIso?: string): ScanRow[];

  // blocklist
  listBlocklist(): { docNumber?: string; name?: string; dob?: string; reason: string }[];

  // persistence hook for adapters that batch writes (memory→file/IndexedDB). Sheets adapter: no-op.
  flush(): void;
}

/**
 * "Provision over HTTPS" — see `packages/shared/src/api.ts`'s "Provisioning over HTTPS" section
 * for the trust model. Optional on `Platform` so the memory adapter — which has no workbook to
 * create and no triggers to schedule — can simply omit it; `apps/backend/src/core/handlers.ts`
 * falls back to fixed, harmless answers when it's absent. Only `adapters/sheets/platform.ts`'s
 * `createAppsScriptPlatform` implements this for real.
 */
export interface Provisioning {
  isInitialised(): boolean;
  /** Idempotent: creates the workbook/Drive tree only if missing, otherwise just re-reads the ids. */
  setup(): { sheetId: string; driveFolderId: string; created: boolean; tabs: string[] };
  /** Installs (or re-installs) the nightly export trigger; returns the handler names left installed. */
  installTriggers(): string[];
  listTriggers(): string[];
}

export interface Platform {
  sha256(input: string): string; // hex
  sha256B64url(input: string): string;
  hmacSha256B64url(message: string, key: string): string;
  uuid(): string;
  now(): string; // ISO timestamp
  randomToken(bytes: number): string; // base64url
  withLock<T>(fn: () => T): T; // serialises all mutations; throws BusyError after ~30 s
  sendMail(to: string, subject: string, html: string, text?: string): void;
  log(...args: unknown[]): void;
  runtime: 'appsscript' | 'devserver' | 'demo';
  provisioning?: Provisioning;
}

export class BusyError extends Error {
  code = 'busy' as const;
  constructor() {
    super('busy, retry');
  }
}
